import React from "react";
import { getVisitor } from "../services/api";
import { useParams } from "react-router-dom";
import moment from "moment";
import LoadingIndicator from "../components/LoadingIndicator";

const VisitorPage = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const { qrCode } = useParams();

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getVisitor(qrCode);
        setData(response.data.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    if (qrCode) {
      fetchData();
    } else {
      setLoading(false);
      setError({ message: "QR Code tidak ditemukan" });
    }
  }, [qrCode]);

  const checkStatusDate = (startUtc, endUtc) => {
    const now = moment();
    const start = moment.utc(startUtc).local();
    const end = moment.utc(endUtc).local();

    // Date-only comparison (ignoring time)
    const nowDate = now.clone().startOf("day");
    const startDate = start.clone().startOf("day");
    const endDate = end.clone().startOf("day");

    if (nowDate.isBefore(startDate)) {
      return "TOOEARLY";
    }
    if (nowDate.isAfter(endDate)) {
      return "EXPIRED";
    }

    // Time-inclusive comparison (since dates match)
    if (now.isBefore(start)) {
      return "WAITING";
    }
    if (now.isAfter(end)) {
      return "INVALID";
    }

    return "VALID";
  };

  const status = data ? checkStatusDate(data.startDate, data.endDate) : null;

  const getStatusConfig = (status) => {
    switch (status) {
      case "VALID":
        return {
          label: "VALID",
          title: "AKSES DIIZINKAN",
          textColor: "#00A63E",
          bgColor: "#DCFCE7",
          icon: "/assets/CheckList.svg",
        };
      case "WAITING":
        return {
          label: "WAITING",
          title: "MENUNGGU JADWAL",
          textColor: "#E17100",
          bgColor: "#FEF3C6",
          icon: "/assets/Timer.svg",
        };
      case "INVALID":
        return {
          label: "INVALID",
          title: "AKSES KADALUARSA",
          textColor: "#EC003F",
          bgColor: "#FFE4E6",
          icon: "/assets/Info.svg",
        };
      case "EXPIRED":
        return {
          label: "EXPIRED",
          title: "AKSES KADALUARSA",
          textColor: "#EC003F",
          bgColor: "#FFE4E6",
          icon: "/assets/Info.svg",
        };
      case "TOOEARLY":
        return {
          label: "TOO EARLY",
          title: "MENUNGGU JADWAL",
          textColor: "#E17100",
          bgColor: "#FEF3C6",
          icon: "/assets/Timer.svg",
        };
      default:
        return null;
    }
  };

  const getIndoTimezone = (date) => {
    const offset = moment(date).utcOffset(); // dalam menit

    switch (offset) {
      case 420: // UTC+7
        return "WIB";
      case 480: // UTC+8
        return "WITA";
      case 540: // UTC+9
        return "WIT";
      default:
        return "Local Time";
    }
  };

  const statusConfig = getStatusConfig(status);

  if (loading) return <LoadingIndicator />;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="bg-[#F5F5F5] min-h-screen flex flex-col items-center md:justify-center md:p-20">
      <div className="container max-w-4xl px-8 pt-24 pb-10 md:py-20 bg-white md:rounded-xl md:rounded-b-none ">
        <div className="flex-row items-center flex gap-5">
          <img
            src={data?.housingEstate?.logoUrl}
            alt="visitor"
            className="md:h-24 md:w-24 w-16 h-16 bg-gray-200"
          />
          <div className="space-y-1">
            <h1 className="md:text-3xl text-2xl font-bold text-gray-900">
              {data?.housingEstate?.name}
            </h1>
            <p className="text-sm text-[#737373]">
              {data?.housingEstate?.address}
            </p>
          </div>
        </div>
      </div>
      <div
        style={{ backgroundColor: statusConfig.bgColor }}
        className={`container max-w-4xl px-8 py-4 md:rounded-xl md:rounded-t-none`}
      >
        <div className="flex-row flex items-start gap-4">
          <img src={statusConfig.icon} alt="check" />
          <div>
            <p
              style={{ color: statusConfig.textColor }}
              className="text-sm font-medium text-gray-700"
            >
              {statusConfig.title}
            </p>
            {statusConfig.label === "VALID" && (
              <p className="text-sm font-medium text-gray-700">
                (Berlaku s/d {moment.utc(data.endDate).local().format("HH:mm")}{" "}
                {getIndoTimezone(data.endDate)})
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="container max-w-4xl bg-transparent rounded-xl rounded-b-none md:flex md:p-0 p-6 items-start gap-6 md:mt-8">
        <div className="w-full p-5 bg-white rounded-xl">
          <p className="text-lg font-bold">Detail Kunjungan</p>
          <div className="mt-5">
            <div className="flex-row flex items-center gap-2">
              <img src="/assets/Sova.svg" alt="sova" />
              <p className="text-xs text-gray-500">Tujuan</p>
            </div>
            <p className="text-md text-black my-1">
              {data?.house?.description} - {data?.house?.houseCode}
            </p>
            <p className="text-xs text-gray-500">{data?.house?.address}</p>
          </div>
          <div className="mt-5">
            <div className="flex-row flex items-center gap-2">
              <img src="/assets/Date.svg" alt="date" />
              <p className="text-xs text-gray-500">Waktu Kunjungan</p>
            </div>
            <p className="text-md text-black my-1">
              {moment.utc(data.startDate).local().format("dddd, DD MMMM YYYY")}
            </p>
            <p className="text-xs text-gray-500">
              {moment.utc(data.startDate).local().format("HH:mm")} -{" "}
              {moment.utc(data.endDate).local().format("HH:mm")}{" "}
              {getIndoTimezone(data.endDate)}
            </p>
          </div>
        </div>
        <div className="w-full p-5 bg-white rounded-xl mt-6 md:mt-0">
          <p className="text-lg font-bold">Daftar Tamu</p>
          <div className="mt-5 flex-row flex items-center justify-between">
            <div>
              <div className="flex-row flex items-center gap-2">
                <img src="/assets/Chat.svg" alt="date" />
                <p className="text-xs text-gray-500">Kontak Utama</p>
              </div>
              <p className="text-md text-black my-1">{data?.phone}</p>
            </div>
            <div
              onClick={() => window.open(`https://wa.me/${data?.phone}`)}
              className="bg-[#009689] px-3 py-2 rounded-lg flex-row flex items-center gap-2 cursor-pointer"
            >
              <img src="/assets/TopUp.svg" alt="chat" />
              <p className="text-white text-xs">Hubungi</p>
            </div>
          </div>
          <div className="mt-5">
            <div className="flex-row flex items-center gap-2">
              <img src="/assets/Person.svg" alt="date" />
              <p className="text-xs text-gray-500">Status</p>
            </div>
            <p className="text-md text-black my-1">{data?.statusInfo}</p>
          </div>
          <div className="mt-5">
            <div className="flex-row flex items-center gap-2">
              <img src="/assets/Person2.svg" alt="date" />
              <p className="text-xs text-gray-500">
                Detail Tamu ({data?.guestList?.length} Orang)
              </p>
            </div>
            {data?.guestList?.map((res, i) => {
              return (
                <div
                  key={i}
                  className="flex flex-row justify-between items-center"
                >
                  <p className="text-md text-black my-1">{res?.name}</p>
                  {res.imgUrl ? (
                    <div
                      onClick={() => window.open(res.imgUrl, "_blank")}
                      className="flex flex-row cursor-pointer items-center"
                    >
                      <p className="text-xs text-[#00A63E] my-1 underline">
                        Foto Identitas Tersedia{" "}
                      </p>
                      <img src="/assets/ArrowRight.svg" className="h-4" />
                    </div>
                  ) : (
                    <p className="text-xs text-[#A1A1A1] my-1">
                      Tanpa Foto Identitas
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorPage;
